// Bonus · luxury black-and-gold member card
// (extracted from HeroArtwork.jsx; behaviour preserved)
import React from "react";
import { motion } from "framer-motion";
import { Check } from "lucide-react";
import { useTranslation } from "react-i18next";
import { glassCard, GOLD } from "./_shared";

function BonusArt({ accent = GOLD }) {
  const { t } = useTranslation();
  return (
    <div
      className="relative w-full aspect-square max-w-[460px] mx-auto flex items-center justify-center overflow-hidden"
      data-testid="hero-art-bonus"
      style={{ perspective: "1400px" }}
    >
      {/* Gold halo · DIMMED from 18% to 9% */}
      <div
        aria-hidden
        className="absolute inset-[16%] pointer-events-none"
        style={{
          background: `radial-gradient(ellipse 65% 45% at 50% 55%, color-mix(in srgb, ${accent} 9%, transparent) 0%, transparent 70%)`,
          filter: "blur(14px)",
        }}
      />

      {/* Cascading gold particles · subtle count */}
      {[...Array(14)].map((_, i) => {
        // The visual position + animation delay are derived from `i`, so
        // the index IS the stable identity for each particle here. We
        // namespace it to make the intent explicit for code reviewers.
        const a = (i / 14) * Math.PI * 2 + (i % 3) * 0.4;
        const r = 36 + (i % 4) * 5;
        return (
          <motion.span
            key={`particle-${i}`}
            aria-hidden
            className="absolute rounded-full pointer-events-none"
            style={{
              left: `${50 + Math.cos(a) * r}%`,
              top:  `${50 + Math.sin(a) * r}%`,
              width: 3, height: 3,
              background: accent,
              boxShadow: `0 0 4px ${accent}`,
            }}
            animate={{ opacity: [0.08, 0.45, 0.08], scale: [0.5, 1.1, 0.5] }}
            transition={{ duration: 2.4 + (i % 5) * 0.3, repeat: Infinity, ease: "easeInOut", delay: i * 0.18 }}
          />
        );
      })}

      {/* THE MEMBER CARD · 1.59:1 credit-card aspect, tilted in 3D · softer shadow */}
      <motion.div
        className="relative rounded-2xl overflow-hidden"
        style={{
          width: "84%",
          aspectRatio: "1.59",
          background: `
            linear-gradient(135deg,
              #0c0a05 0%,
              #221806 28%,
              #382608 50%,
              #221806 72%,
              #0c0a05 100%)`,
          border: `1px solid color-mix(in srgb, ${accent} 38%, transparent)`,
          boxShadow: `
            0 14px 32px -22px color-mix(in srgb, ${accent} 16%, transparent),
            0 8px 18px -10px rgba(0,0,0,0.4),
            inset 0 1px 0 color-mix(in srgb, ${accent} 22%, transparent),
            inset 0 -2px 0 rgba(0,0,0,0.3)`,
          transform: "rotateY(-9deg) rotateX(6deg)",
          transformStyle: "preserve-3d",
        }}
        animate={{ y: [0, -6, 0] }}
        transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
      >
        {/* Diagonal shimmer · travels across the card */}
        <motion.div
          aria-hidden
          className="absolute inset-y-0 pointer-events-none"
          style={{
            width: "32%",
            background: "linear-gradient(105deg, transparent 35%, rgba(255,240,200,0.30) 50%, transparent 65%)",
            mixBlendMode: "screen",
          }}
          animate={{ x: ["-120%", "320%"] }}
          transition={{ duration: 3.4, repeat: Infinity, ease: "easeInOut", repeatDelay: 1.6 }}
        />

        {/* Subtle guilloché pattern · concentric circles for premium feel */}
        <svg aria-hidden viewBox="0 0 320 200" className="absolute inset-0 w-full h-full opacity-20 pointer-events-none">
          {[...Array(8)].map((_, i) => (
            <circle key={`guilloche-${i}`} cx="240" cy="100" r={80 + i * 14}
                    fill="none" stroke={accent} strokeWidth="0.35" />
          ))}
        </svg>

        {/* Top row · brand mark + chip */}
        <div className="absolute inset-x-0 top-0 px-5 pt-4 flex items-start justify-between">
          <div>
            <div className="font-mono font-bold uppercase tracking-[0.22em]"
                 style={{ fontSize: 9, color: accent, opacity: 0.92 }}>
              OFINANCIAL MARKETS
            </div>
            <div className="font-mono mt-0.5"
                 style={{ fontSize: 7.5, color: "#a07d20", letterSpacing: "0.18em" }}>
              MEMBER · FIRST DEPOSIT BONUS
            </div>
          </div>
          {/* Gold chip */}
          <div
            className="rounded-md grid place-items-center"
            style={{
              width: 32, height: 26,
              background: `linear-gradient(135deg, #f3d671 0%, ${accent} 50%, #8c6a18 100%)`,
              boxShadow: "inset 0 1px 0 rgba(255,250,200,0.5), inset 0 -1px 0 rgba(80,55,10,0.5)",
            }}
          >
            <svg viewBox="0 0 16 12" className="w-4 h-3">
              <g stroke="#5e4205" strokeWidth="0.4" fill="none" opacity="0.55">
                <line x1="0" y1="3" x2="16" y2="3" />
                <line x1="0" y1="6" x2="16" y2="6" />
                <line x1="0" y1="9" x2="16" y2="9" />
                <line x1="4" y1="0" x2="4" y2="12" />
                <line x1="8" y1="0" x2="8" y2="12" />
                <line x1="12" y1="0" x2="12" y2="12" />
              </g>
            </svg>
          </div>
        </div>

        {/* Centre · BIG "+50%" rendered as SVG so the OUTLINE of the
            digits can be a true gold gradient stroke (light gold at the
            top, bright gold in the middle, deep gold at the bottom)
            around a dark embossed fill. A second text layer carries an
            animated bright-band gradient that sweeps across the digits
            for a moving highlight. */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <svg
            viewBox="0 0 320 130"
            preserveAspectRatio="xMidYMid meet"
            className="w-[72%] h-auto select-none"
            style={{ overflow: "visible" }}
            aria-hidden
          >
            <defs>
              {/* Outline gradient · light gold → bright gold → deep gold */}
              <linearGradient id="bonus50-stroke" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%"   stopColor="#fff5c8" />
                <stop offset="35%"  stopColor="#f3d671" />
                <stop offset="65%"  stopColor={accent} />
                <stop offset="100%" stopColor="#8c6a18" />
              </linearGradient>
              {/* Dark embossed fill */}
              <linearGradient id="bonus50-fill" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%"   stopColor="#1a1106" />
                <stop offset="55%"  stopColor="#0a0703" />
                <stop offset="100%" stopColor="#221806" />
              </linearGradient>
              {/* Sweeping bright shine band · narrow gradient slid
                  horizontally across the bounding box via
                  animateTransform on the gradient itself. */}
              <linearGradient id="bonus50-shine" x1="0" y1="0" x2="0.55" y2="0">
                <stop offset="0%"   stopColor="rgba(255,253,230,0)" />
                <stop offset="50%"  stopColor="rgba(255,253,230,0.95)" />
                <stop offset="100%" stopColor="rgba(255,253,230,0)" />
                <animateTransform
                  attributeName="gradientTransform"
                  type="translate"
                  from="-1.6 0"
                  to="2 0"
                  dur="3.4s"
                  repeatCount="indefinite"
                />
              </linearGradient>
            </defs>

            {/* Bottom layer · dark fill + thick GOLD GRADIENT OUTLINE */}
            <text
              x="50%"
              y="50%"
              textAnchor="middle"
              dominantBaseline="central"
              fill="url(#bonus50-fill)"
              stroke="url(#bonus50-stroke)"
              strokeWidth="2.2"
              strokeLinejoin="round"
              paintOrder="stroke fill"
              style={{
                fontFamily: "'Outfit', 'Sora', system-ui, sans-serif",
                fontWeight: 900,
                fontSize: "92px",
                letterSpacing: "-0.04em",
              }}
            >
              +50%
            </text>

            {/* Top layer · animated bright shine band masked to the glyphs */}
            <text
              x="50%"
              y="50%"
              textAnchor="middle"
              dominantBaseline="central"
              fill="url(#bonus50-shine)"
              style={{
                fontFamily: "'Outfit', 'Sora', system-ui, sans-serif",
                fontWeight: 900,
                fontSize: "92px",
                letterSpacing: "-0.04em",
                pointerEvents: "none",
              }}
            >
              +50%
            </text>
          </svg>
        </div>

        {/* Bottom · stylised number + max bonus */}
        <div className="absolute inset-x-0 bottom-0 px-5 pb-4 flex items-end justify-between">
          <div className="font-mono tabular-nums" style={{ fontSize: 11, color: accent, letterSpacing: "0.32em" }}>
            OFM<span className="mx-2 opacity-50">••</span>
            <span style={{ color: "#fff5c8" }}>BONUS</span>
            <span className="mx-2 opacity-50">••</span>5000
          </div>
          <div className="text-right">
            <div className="font-mono uppercase" style={{ fontSize: 7.5, color: "#a07d20", letterSpacing: "0.22em" }}>
              {t("heroArt.maxBonus", "MAX BONUS")}
            </div>
            <div className="font-display font-bold tabular-nums" style={{ fontSize: 14, color: "#fff5c8" }}>
              $5,000
            </div>
          </div>
        </div>
      </motion.div>

      {/* Floating "BONUS APPLIED ✓" toast above the card */}
      <motion.div
        className="absolute rounded-xl px-3 py-2 flex items-center gap-2"
        style={{
          ...glassCard(accent, 92),
          top: "8%", right: "8%",
          transform: "rotateY(-6deg) rotateX(8deg)",
        }}
        initial={{ opacity: 0, y: -8 }}
        animate={{ opacity: 1, y: [-8, -4, -8] }}
        transition={{ opacity: { duration: 0.5, delay: 0.8 }, y: { duration: 4, repeat: Infinity, ease: "easeInOut", delay: 1.4 } }}
      >
        <div className="w-6 h-6 rounded-full grid place-items-center shrink-0"
             style={{ background: `color-mix(in srgb, ${accent} 18%, transparent)`, border: `1px solid color-mix(in srgb, ${accent} 60%, transparent)` }}>
          <Check size={11} style={{ color: accent }} />
        </div>
        <div className="leading-tight">
          <div className="font-mono font-bold uppercase tracking-wider" style={{ fontSize: 8.5, color: "var(--text-1)" }}>{t("heroArt.bonusApplied", "BONUS APPLIED")}</div>
          <div className="font-mono tabular-nums mt-0.5" style={{ fontSize: 8.5, color: accent }}>+$500.00 {t("heroArt.creditedShort", "credited")}</div>
        </div>
      </motion.div>
    </div>
  );
}


export default BonusArt;
